import { useEffect, useState } from "react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

import { systemStatus, type SystemStatus } from "@/lib/api";

/** Richer admin-only version of the header SystemStatusBar. Renders
 *  the full /api/system/status payload as field/value pairs so admins
 *  can see tracker baselines + subscribers + last poll / last error
 *  and scanner progress + circuit breaker + last error side by side. */
export function SystemStatusPanel() {
  const [status, setStatus] = useState<SystemStatus | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const s = await systemStatus();
        if (!cancelled) {
          setStatus(s);
          setError(null);
        }
      } catch (e: unknown) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      }
    };
    void tick();
    const t = setInterval(() => void tick(), 5_000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, []);

  return (
    <Card>
      <CardHeader>
        <CardTitle>System status</CardTitle>
        <p className="text-xs text-muted-foreground">
          Refreshes every 5s. Same payload as the header pill, expanded.
        </p>
      </CardHeader>
      <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
        {error && (
          <p className="text-rose-500 md:col-span-2">{error}</p>
        )}
        <div className="space-y-1">
          <div className="text-muted-foreground font-semibold">Tracker</div>
          {status?.tracker ? (
            <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 font-mono">
              <dt className="text-muted-foreground">Baselines</dt>
              <dd>{status.tracker.baselines}</dd>
              <dt className="text-muted-foreground">Subscribers</dt>
              <dd>{status.tracker.subscribers}</dd>
              <dt className="text-muted-foreground">Interval</dt>
              <dd>{status.tracker.interval_sec}s</dd>
              <dt className="text-muted-foreground">Market</dt>
              <dd>{status.tracker.market_open ? "open" : "closed"}</dd>
              <dt className="text-muted-foreground">Last poll</dt>
              <dd>
                {status.tracker.last_poll_at
                  ? new Date(status.tracker.last_poll_at).toLocaleTimeString()
                  : "—"}
              </dd>
              {status.tracker.last_error && (
                <>
                  <dt className="text-muted-foreground">Last error</dt>
                  <dd className="text-rose-500">{status.tracker.last_error}</dd>
                </>
              )}
            </dl>
          ) : (
            <p className="text-muted-foreground">Disabled / not wired.</p>
          )}
        </div>
        <div className="space-y-1">
          <div className="text-muted-foreground font-semibold">Scanner</div>
          {status?.scanner ? (
            <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 font-mono">
              <dt className="text-muted-foreground">Running</dt>
              <dd>{status.scanner.running ? "yes" : "no"}</dd>
              <dt className="text-muted-foreground">Universe</dt>
              <dd>{status.scanner.universe_size.toLocaleString()}</dd>
              <dt className="text-muted-foreground">Scanned</dt>
              <dd>
                {status.scanner.scanned.toLocaleString()} /{" "}
                {status.scanner.universe_size.toLocaleString()}
              </dd>
              <dt className="text-muted-foreground">Queue</dt>
              <dd>{status.scanner.queue_depth.toLocaleString()}</dd>
              <dt className="text-muted-foreground">Errored</dt>
              <dd>{status.scanner.errored.toLocaleString()}</dd>
              {status.scanner.circuit_open && (
                <>
                  <dt className="text-muted-foreground">Circuit</dt>
                  <dd className="text-rose-500">
                    open · {status.scanner.circuit_cooldown_sec}s
                  </dd>
                </>
              )}
              {status.scanner.last_error && (
                <>
                  <dt className="text-muted-foreground">Last error</dt>
                  <dd className="text-rose-500">{status.scanner.last_error}</dd>
                </>
              )}
            </dl>
          ) : (
            <p className="text-muted-foreground">Disabled / not wired.</p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
